import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";

function regionLabel(country) {
  const c = (country || "").toLowerCase().trim();
  if (c === "canada" || c.includes("canada")) return "Province";
  if (c === "ireland" || c.includes("ireland")) return "County";
  if (
    c === "united states" ||
    c === "united states of america" ||
    c === "usa" ||
    c === "us" ||
    c.includes("united states")
  )
    return "State";
  return "Region";
}

export default function ClientEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    addressLine1: "",
    addressLine2: "",
    country: "",
    region: "",
    city: "",
    postalCode: "",
    notes: "",
  });

  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "clients", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setClient(next);
        setLoadingClient(false);
      },
      () => {
        setClient(null);
        setLoadingClient(false);
      }
    );

    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!client) return;
    setForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      organization: client.organization || "",
      addressLine1: client.addressLine1 || "",
      addressLine2: client.addressLine2 || "",
      country: client.country || "",
      region: client.region || "",
      city: client.city || "",
      postalCode: client.postalCode || "",
      notes: client.notes || "",
    });
  }, [client]);

  async function saveClient(e) {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, "clients", id), {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        organization: form.organization.trim(),
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim(),
        country: form.country.trim(),
        region: form.region.trim(),
        city: form.city.trim(),
        postalCode: form.postalCode.trim(),
        notes: form.notes,
        lastUpdatedAt: serverTimestamp(),
      });

      navigate(`/admin/clients/${id}`);
    } finally {
      setSaving(false);
    }
  }

  async function deleteClient() {
    if (!id) return;
    const ok = window.confirm("Delete this client? This cannot be undone.");
    if (!ok) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "clients", id));
      navigate("/admin/clients");
    } finally {
      setDeleting(false);
    }
  }

  if (loadingClient) return <p className="muted">Loading...</p>;

  if (!client) {
    return (
      <div>
        <p className="muted">Client not found.</p>
        <Link className="btn" to="/admin/clients">
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>Edit Client</h2>
          <div className="micro muted">{client.name || "Client"}</div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Link className="btn" to={`/admin/clients/${id}`}>
            Cancel
          </Link>
          <button className="btn" type="button" onClick={deleteClient} disabled={saving || deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </button>
          <button className="btn primary" type="submit" form="client-edit-form" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <form id="client-edit-form" onSubmit={saveClient} className="form">
          <label className="field">
            <input
              type="text"
              placeholder="Client Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              disabled={saving}
            />
          </label>

          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <label className="field">
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                disabled={saving}
              />
            </label>

            <label className="field">
              <input
                type="text"
                placeholder="Phone"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                disabled={saving}
              />
            </label>
          </div>

          <label className="field">
            <input
              type="text"
              placeholder="Organization (optional)"
              value={form.organization}
              onChange={(e) => setForm((p) => ({ ...p, organization: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder="Address line 1"
              value={form.addressLine1}
              onChange={(e) => setForm((p) => ({ ...p, addressLine1: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder="Address line 2"
              value={form.addressLine2}
              onChange={(e) => setForm((p) => ({ ...p, addressLine2: e.target.value }))}
              disabled={saving}
            />
          </label>

          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <label className="field">
              <input
                type="text"
                placeholder="Country"
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
                disabled={saving}
              />
            </label>

            <label className="field">
              <input
                type="text"
                placeholder={regionLabel(form.country)}
                value={form.region}
                onChange={(e) => setForm((p) => ({ ...p, region: e.target.value }))}
                disabled={saving}
              />
            </label>

            <label className="field">
              <input
                type="text"
                placeholder="City"
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                disabled={saving}
              />
            </label>
          </div>

          <label className="field">
            <input
              type="text"
              placeholder="Postal code"
              value={form.postalCode}
              onChange={(e) => setForm((p) => ({ ...p, postalCode: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <textarea
              rows="4"
              placeholder="Client notes"
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              disabled={saving}
            />
          </label>
        </form>
      </div>
    </div>
  );
}
